/**
 * Vérifier l'impression — et voir le DOCUMENT, pas une imitation.
 *
 * DEUX VÉRITÉS, UN SEUL DOCUMENT. Cet aperçu était rendu par un composant
 * React qui reconstruisait la fiche (structure, styles, pagination) à partir
 * des exercices. Le PDF, lui, est composé par WeasyPrint à partir du gabarit
 * du serveur. Deux implémentations d'un même document finissent toujours par
 * diverger : une police, une marge, une coupure — et l'aperçu ment.
 *
 * Ici, l'aperçu affiche le HTML QUE LE PDF IMPRIMERA, tel que le serveur le
 * fabrique, dans un cadre à la largeur exacte d'une A4 (794 px CSS) avec les
 * marges du gabarit. Et le nombre de feuilles est compté par WeasyPrint
 * lui-même, pas estimé par le navigateur.
 *
 * IL RESTE UNE DIFFÉRENCE, ET UNE SEULE : à l'écran le document défile d'un
 * trait, alors que l'imprimante le coupe en feuilles. Le nombre de feuilles
 * est donc exact, l'endroit des coupures n'est pas montré. C'est écrit sous
 * l'aperçu plutôt que dissimulé.
 *
 * LE ZOOM EST UN VRAI ZOOM. L'ancien réglait la largeur d'un conteneur que le
 * composant ramenait ensuite à `min(1, largeur/794)` : au-delà de 100 %, le
 * bouton « + » changeait l'étiquette sans rien agrandir. Ici l'échelle est
 * appliquée au document lui-même, et l'étiquette dit ce qu'on voit.
 *
 * Un aperçu ne consomme jamais le quota : la route ne décompte rien.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { ArrowLeft, Minus, Plus, Maximize2, Loader2, RefreshCw } from "lucide-react";
import { API_FICHES, corpsExport, entetesFiche } from "./useFicheH";

/** A4 en pixels CSS à 96 ppp — la largeur dans laquelle WeasyPrint compose. */
export const LARGEUR_A4 = 794;
export const HAUTEUR_A4 = 1123;

const PAS = 0.15;
const MIN = 0.3;
const MAX = 2;

/**
 * Le document, préparé pour un écran — sans toucher à sa mise en page.
 *
 * `@page { margin: 2cm }` ne s'applique qu'à l'impression : à l'écran il faut
 * redonner au corps la largeur et les marges de la feuille, sans quoi le
 * texte s'étalerait sur toute la largeur du cadre et l'aperçu mentirait sur
 * les retours à la ligne. `box-sizing: border-box` est déjà posé par le
 * gabarit : 794 px de large moins 2 × 2 cm donnent le bloc de texte du PDF.
 */
export function habillerPourEcran(html) {
  const style =
    `<style id="lmm-apercu-ecran">` +
    `html{background:transparent;}` +
    `body{width:${LARGEUR_A4}px;min-height:${HAUTEUR_A4}px;padding:2cm;` +
    `background:#fff;margin:0;}` +
    `</style>`;
  return html.includes("</head>")
    ? html.replace("</head>", `${style}</head>`)
    : style + html;
}

export default function ApercuPapier({ fiche, exercices, onFermer }) {
  const [vue, setVue] = useState("sujet");
  const [zoom, setZoom] = useState(null); // null = ajusté à la largeur
  const [documents, setDocuments] = useState({});   // { sujet: {...}, corrige: {...} }
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [hauteur, setHauteur] = useState(HAUTEUR_A4);
  const [largeurCadre, setLargeurCadre] = useState(LARGEUR_A4);

  const cadre = useRef(null);
  const iframe = useRef(null);

  /* LA MÊME VERSION POUR LE SUJET ET POUR LE CORRIGÉ.
   *
   * Les exercices sont figés à l'ouverture. Sans cela, un remplacement qui
   * arriverait pendant qu'on regarde ferait cohabiter un sujet d'une version
   * et un corrigé d'une autre — le défaut même qu'on corrige. */
  const versionFigee = useRef({ fiche, exercices });

  const charger = useCallback(async (quelleVue) => {
    setChargement(true);
    setErreur(null);
    try {
      const { fiche: f, exercices: ex } = versionFigee.current;
      const { data } = await axios.post(
        `${API_FICHES}/apercu`,
        corpsExport(f, ex, quelleVue === "corrige"),
        { headers: entetesFiche() }
      );
      setDocuments((d) => ({ ...d, [quelleVue]: data }));
    } catch (e) {
      setErreur(
        e?.response?.data?.detail?.message ||
          "L'aperçu n'a pas pu être préparé. Le téléchargement, lui, reste possible."
      );
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (!documents[vue]) charger(vue);
  }, [vue, documents, charger]);

  /* La largeur disponible décide de l'échelle « ajusté ». */
  useLayoutEffect(() => {
    const el = cadre.current;
    if (!el) return undefined;
    const mesurer = () => {
      const style = window.getComputedStyle(el);
      const marges = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      setLargeurCadre(Math.max(1, (el.clientWidth || LARGEUR_A4) - marges));
    };
    mesurer();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", mesurer);
      return () => window.removeEventListener("resize", mesurer);
    }
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(el);
    return () => observateur.disconnect();
  }, []);

  const document_ = documents[vue];
  const feuilles = document_?.feuilles ?? null;
  const feuillesMax = document_?.feuilles_max ?? null;

  const echelle = useMemo(() => {
    if (zoom !== null) return zoom;
    // « Ajusté » = la feuille entre dans le cadre. Jamais au-delà de 100 % :
    // agrandir sans qu'on l'ait demandé tromperait sur la taille réelle.
    return Math.max(MIN, Math.min(1, largeurCadre / LARGEUR_A4));
  }, [zoom, largeurCadre]);

  /* La hauteur du document, mesurée dans le cadre lui-même. À défaut — un
   * navigateur qui refuse l'accès au document embarqué —, le nombre de
   * feuilles COMPTÉ PAR LE SERVEUR donne une hauteur juste. */
  const mesurerHauteur = useCallback(() => {
    const cadreHtml = iframe.current;
    try {
      const doc = cadreHtml?.contentDocument;
      const h = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight;
      if (h && h > 0) {
        setHauteur(h);
        return;
      }
    } catch (_) { /* document inaccessible : on retombe sur le compte serveur */ }
    setHauteur(Math.max(1, feuilles || 1) * HAUTEUR_A4);
  }, [feuilles]);

  useEffect(() => {
    if (!document_) return undefined;
    // Les polices peuvent arriver après le `load` et changer la hauteur.
    const t = setTimeout(mesurerHauteur, 250);
    return () => clearTimeout(t);
  }, [document_, mesurerHauteur]);

  const srcDoc = useMemo(
    () => (document_?.html ? habillerPourEcran(document_.html) : null),
    [document_]
  );

  const ajuste = zoom === null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <header className="flex items-center gap-2 border-b border-border bg-card px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onFermer} className="shrink-0">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Retour
        </Button>
        <div className="flex-1 truncate text-sm font-medium">Aperçu papier</div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant={vue === "sujet" ? "default" : "outline"}
            size="sm"
            onClick={() => setVue("sujet")}
          >
            Sujet
          </Button>
          <Button
            variant={vue === "corrige" ? "default" : "outline"}
            size="sm"
            onClick={() => setVue("corrige")}
          >
            Corrigé
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-1.5">
        <Button
          variant="outline" size="sm" aria-label="Réduire"
          onClick={() => setZoom((z) => Math.max(MIN, (z ?? echelle) - PAS))}
          disabled={echelle <= MIN + 0.001}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="min-w-[4.5rem] text-center text-xs tabular-nums text-muted-foreground">
          {/* L'étiquette dit l'échelle RÉELLEMENT appliquée, y compris en
              mode ajusté : « ajusté » seul laissait croire à 100 %. */}
          {ajuste ? `ajusté · ${Math.round(echelle * 100)} %` : `${Math.round(echelle * 100)} %`}
        </span>
        <Button
          variant="outline" size="sm" aria-label="Agrandir"
          onClick={() => setZoom((z) => Math.min(MAX, (z ?? echelle) + PAS))}
          disabled={echelle >= MAX - 0.001}
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost" size="sm" onClick={() => setZoom(null)}
          disabled={ajuste}
          className="ml-auto"
        >
          <Maximize2 className="mr-1 h-4 w-4" />
          Ajuster
        </Button>
      </div>

      {feuilles !== null && (
        <p
          className={
            "border-b border-border px-3 py-1.5 text-center text-xs " +
            (feuillesMax && feuilles > feuillesMax
              ? "bg-destructive/10 text-destructive"
              : "bg-card text-muted-foreground")
          }
          data-testid="compte-feuilles"
        >
          {feuilles} feuille{feuilles > 1 ? "s" : ""} à l'impression
          {feuillesMax && feuilles > feuillesMax
            ? ` — le téléchargement sans compte s'arrête à ${feuillesMax}.`
            : ""}
        </p>
      )}

      {/* Le zoom ne déborde que DANS ce conteneur : le reste de l'application
          garde sa largeur, quel que soit l'agrandissement. */}
      <div ref={cadre} className="flex-1 overflow-auto bg-muted/30 p-3">
        {erreur ? (
          <div className="mx-auto max-w-sm py-12 text-center">
            <p className="text-sm text-muted-foreground">{erreur}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => charger(vue)}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Réessayer
            </Button>
          </div>
        ) : !srcDoc ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Préparation de l'aperçu…
          </div>
        ) : (
          <div
            className="mx-auto shadow-sm"
            style={{
              width: LARGEUR_A4 * echelle,
              height: hauteur * echelle,
              maxWidth: "none",
            }}
          >
            <iframe
              ref={iframe}
              title={`Aperçu ${vue === "corrige" ? "du corrigé" : "du sujet"}`}
              srcDoc={srcDoc}
              /* Le document vient de notre serveur et n'a aucun script : on
                 lui refuse l'exécution, et on garde `allow-same-origin` pour
                 pouvoir mesurer sa hauteur. */
              sandbox="allow-same-origin"
              scrolling="no"
              onLoad={mesurerHauteur}
              style={{
                width: LARGEUR_A4,
                height: hauteur,
                border: 0,
                background: "#fff",
                transform: `scale(${echelle})`,
                transformOrigin: "top left",
                display: "block",
              }}
            />
          </div>
        )}
        {chargement && srcDoc && (
          <p className="py-2 text-center text-xs text-muted-foreground">Mise à jour…</p>
        )}
      </div>

      <p className="border-t border-border bg-card px-3 py-2 text-center text-xs text-muted-foreground">
        C'est le document que le PDF imprimera. Le zoom ne le change pas. À
        l'écran il défile d'un trait : le nombre de feuilles est exact, les
        coupures ne sont pas figurées.
      </p>
    </div>
  );
}
